import React, { useState } from 'react';
import { HeartPulse, ShoppingBag, Coins, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import { Badge, Card } from '../ui';

export const DoctorTerraShowcase: React.FC<{ onOpenConsultation?: () => void }> = ({ onOpenConsultation }) => {
  const [activeFeature, setActiveFeature] = useState<'health' | 'shopping' | 'earnings' | 'tiara'>('health');

  const features = [
    {
      id: 'health',
      icon: <HeartPulse className="text-[#08adae]" size={20} />,
      title: 'Health & Wellness Vault',
      description: 'Personalized health record management and wellness tracking for Doctor Terra members.',
      tag: 'Digital Health',
    },
    {
      id: 'shopping',
      icon: <ShoppingBag className="text-[#4a82c2]" size={20} />,
      title: 'Integrated Marketplace',
      description: 'In-app digital shopping for curated health products, supplements, and wellness essentials.',
      tag: 'E-Commerce',
    },
    {
      id: 'earnings',
      icon: <Coins className="text-amber-500 dark:text-amber-400" size={20} />,
      title: 'Member Earnings Engine',
      description: 'Real-time multi-tier commission tracking, referral rewards, and instant payout dashboard.',
      tag: 'Fintech Rewards',
    },
    {
      id: 'tiara',
      icon: <Sparkles className="text-purple-600 dark:text-purple-400" size={20} />,
      title: 'Tiara AI Companion',
      description: 'Interactive membership companion guiding members through onboarding and health goals.',
      tag: 'AI Companion',
    },
  ];

  return (
    <div className="relative w-full max-w-xl mx-auto">
      {/* Ambient background glowing mesh */}
      <div className="absolute -top-10 -right-10 w-72 h-72 bg-[#08adae]/20 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-[#4a82c2]/20 rounded-full blur-3xl pointer-events-none" />

      {/* Main Glassmorphic Container */}
      <Card variant="glass" className="relative z-10 p-6 sm:p-8 bg-white/95 dark:bg-[#0f1b2d]/95 border border-slate-200/90 dark:border-[#4a82c2]/30 backdrop-blur-2xl shadow-2xl shadow-blue-950/10 dark:shadow-black/40">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800/80 pb-4 mb-6">
          <div className="flex items-center gap-2">
            <Badge variant="accent" className="text-xs uppercase tracking-wider">
              Flagship Case Study
            </Badge>
            <span className="text-xs font-bold text-[#184166] dark:text-white font-mono">
              Doctor Terra App
            </span>
          </div>
          <span className="text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-2.5 py-0.5 rounded-full">
            50,000+ Members
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-6 items-center">
          {/* Left Column: Doctor Terra Render Image */}
          <div className="sm:col-span-5 flex justify-center">
            <div className="relative group">
              <div className="absolute -inset-1 bg-gradient-to-r from-[#4a82c2] to-[#08adae] rounded-3xl blur opacity-40 group-hover:opacity-75 transition duration-300"></div>
              <img
                src="/assets/screens/Doctor-Terra-App.png"
                alt="Doctor Terra Wellness App by Technook Global"
                className="relative z-10 max-h-[340px] w-auto object-contain rounded-2xl drop-shadow-2xl"
              />
            </div>
          </div>

          {/* Right Column: Interactive Hotspots */}
          <div className="sm:col-span-7 space-y-3">
            {features.map((feat) => {
              const isActive = activeFeature === feat.id;
              return (
                <button
                  key={feat.id}
                  onClick={() => setActiveFeature(feat.id as any)}
                  className={`w-full p-3.5 rounded-2xl border text-left transition-all duration-200 flex items-start gap-3 ${
                    isActive
                      ? 'bg-sky-50 dark:bg-[#182a45] border-[#4a82c2] shadow-md'
                      : 'bg-slate-50 dark:bg-[#0b1322]/60 border-slate-200 dark:border-slate-800/80 hover:border-[#4a82c2]/50'
                  }`}
                >
                  <div className="p-2 rounded-xl bg-white dark:bg-[#09101d] border border-slate-200 dark:border-slate-800 shadow-sm shrink-0">
                    {feat.icon}
                  </div>
                  <div className="flex-1 space-y-1">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-[#184166] dark:text-white">{feat.title}</h4>
                      <span className="text-[10px] font-mono text-[#08adae] font-semibold">{feat.tag}</span>
                    </div>
                    <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-tight">{feat.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Card Footer CTA */}
        <div className="mt-6 pt-4 border-t border-slate-200 dark:border-slate-800/80 flex items-center justify-between">
          <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
            <ShieldCheck size={15} className="text-emerald-500 dark:text-emerald-400" /> Technook Mobile Engine
          </span>
          <button
            onClick={onOpenConsultation}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4a82c2] to-[#184166] hover:opacity-95 text-white text-xs font-bold shadow-md shadow-[#4a82c2]/20 flex items-center gap-1.5 transition-all"
          >
            <span>Build Mobile App</span> <ArrowRight size={13} />
          </button>
        </div>
      </Card>
    </div>
  );
};
